import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, Calculator, Shield, Clock, CheckCircle,
  ArrowRight, HelpCircle, CreditCard, AlertTriangle, Star,
  ChevronDown, User, DollarSign, FileText, BadgeCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// SEO Meta:
// Title: How Pricing Works | ZOLT - Diagnostic Fee, Estimate Ranges, Approve Final Quote
// Description: Understand ZOLT's transparent pricing: Diagnostic Fee at booking, smart estimate ranges upfront, and you approve the final quote after on-site assessment. No extra platform fees.

// Currency helper
const getCurrencySymbol = (currency = 'GBP') => {
  const symbols = { GBP: '£', EUR: '€', USD: '$', INR: '₹', AUD: 'A$', CAD: 'C$' };
  return symbols[currency] || currency;
};

export default function PricingExplainerPage() {
  const currency = 'GBP'; // Can be dynamic based on user location
  const symbol = getCurrencySymbol(currency);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-neutral-600 hover:text-neutral-900">
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How Pricing Works
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-12">
        {/* Hero Section */}
        <motion.section {...fadeIn} className="text-center">
          <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Calculator className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            ZOLT pricing is simple: a Diagnostic Fee at booking to dispatch a contractor, 
            then you approve the final quote after on-site assessment.
          </p>
        </motion.section>

        {/* Pricing Breakdown Cards */}
        <motion.section {...fadeIn} className="space-y-6">
          <h3 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How Pricing Breaks Down
          </h3>

          <div className="grid gap-6">
            {/* Card A: Diagnostic Fee */}
            <div className="bg-white rounded-2xl p-6 border border-neutral-200 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded">STEP 1</span>
                    <h4 className="font-bold text-lg">On-Site Diagnostic & Assessment Fee</h4>
                  </div>
                  <p className="text-neutral-600 mb-4">
                    This covers dispatch and on-site diagnosis by a verified contractor.
                    It&apos;s charged immediately to confirm your booking.
                  </p>
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <BadgeCheck className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-amber-900 font-medium">
                          ZOLT is funded from this fee (6%). Customers don&apos;t pay extra platform fees.
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Example: {symbol}60 diagnostic fee → {symbol}3.60 platform fee, {symbol}56.40 to contractor
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card B: Estimate Ranges */}
            <div className="bg-white rounded-2xl p-6 border border-neutral-200 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500" />
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">STEP 2</span>
                    <h4 className="font-bold text-lg">Estimate Ranges (Before Dispatch)</h4>
                  </div>
                  <p className="text-neutral-600 mb-4">
                    You&apos;ll see labour and materials estimate ranges upfront before you book.
                    These are smart estimates based on job details — not a final quote.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Labour estimate range</p>
                        <p className="text-lg font-bold text-blue-900">{symbol}80 – {symbol}160</p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium mb-1">Materials estimate range</p>
                        <p className="text-lg font-bold text-blue-900">{symbol}20 – {symbol}50</p>
                      </div>
                    </div>
                    <p className="text-xs text-blue-700 mt-3">
                      Estimates help set expectations. Final quote is confirmed after on-site assessment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card C: Final Quote */}
            <div className="bg-white rounded-2xl p-6 border border-neutral-200 overflow-hidden relative">
              <div className="absolute top-0 left-0 w-1 h-full bg-green-500" />
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded">STEP 3</span>
                    <h4 className="font-bold text-lg">Final Quote (After On-Site Assessment)</h4>
                  </div>
                  <p className="text-neutral-600 mb-4">
                    The contractor confirms the final quote after diagnosing your issue on-site.
                    You choose to accept or decline before any work begins.
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-green-900 font-medium">
                          If the quote exceeds the estimate range, the contractor must provide reasons and notes visible to you.
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          You decide with full transparency — no surprises.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Visual Example */}
        <motion.section {...fadeIn} className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h3 className="text-xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Example: Leaking Tap Repair
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-bold text-amber-700">1</span>
                </div>
                <span className="text-neutral-700">Diagnostic Fee (charged at booking)</span>
              </div>
              <span className="font-semibold text-amber-600">{symbol}60</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-bold text-blue-700">2</span>
                </div>
                <span className="text-neutral-700">Labour estimate range</span>
              </div>
              <span className="font-medium text-neutral-600">{symbol}80 – {symbol}120</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-bold text-blue-700">2</span>
                </div>
                <span className="text-neutral-700">Materials estimate range</span>
              </div>
              <span className="font-medium text-neutral-600">{symbol}15 – {symbol}30</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-bold text-green-700">3</span>
                </div>
                <span className="text-neutral-700">Final quote (after assessment)</span>
              </div>
              <span className="font-semibold text-green-600">{symbol}95 labour + {symbol}22 materials</span>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-green-800">Total you pay</span>
                <span className="text-xl font-bold text-green-900">{symbol}177</span>
              </div>
              <p className="text-xs text-green-700 mt-2">
                Diagnostic Fee ({symbol}60) + Labour ({symbol}95) + Materials ({symbol}22) = {symbol}177 total
              </p>
            </div>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section {...fadeIn}>
          <h3 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <HelpCircle className="w-6 h-6" />
            Common Questions
          </h3>

          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="decline" className="bg-white rounded-xl border border-neutral-200 px-4">
              <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                What if I decline the final quote?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 pb-4">
                You won&apos;t be charged for labour or materials. The Diagnostic Fee covers the on-site assessment and dispatch. 
                You keep the assessment insights for future planning, and you&apos;re free to seek other options.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="over-range" className="bg-white rounded-xl border border-neutral-200 px-4">
              <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                What if the final quote is higher than the estimate range?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 pb-4">
                If a quote exceeds the estimate range, the contractor must explain why — those reasons and notes are shown to you before you decide. 
                Common reasons include discovering additional issues or requiring specialist parts. You always have the choice to accept or decline.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="platform-fee" className="bg-white rounded-xl border border-neutral-200 px-4">
              <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                Do customers pay platform fees?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 pb-4">
                No. ZOLT is funded from the Diagnostic Fee (6%). There&apos;s no additional platform fee added at checkout.
                The 6% comes from the Diagnostic Fee you already paid — not from the final labour or materials cost.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="guarantee" className="bg-white rounded-xl border border-neutral-200 px-4">
              <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                Is the estimate a guaranteed price?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 pb-4">
                No — estimates are ranges based on typical jobs of this type. The final quote is confirmed after on-site assessment 
                and always requires your approval. Estimates help set expectations but actual costs depend on the specific situation found.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="diagnostic-purpose" className="bg-white rounded-xl border border-neutral-200 px-4">
              <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                Why is there a Diagnostic Fee?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 pb-4">
                The Diagnostic Fee ensures contractors are compensated for their time to visit, assess, and diagnose your issue — 
                even if you decide not to proceed with the work. It also covers dispatch logistics and secures your booking slot.
                This model creates fair pricing for everyone.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="credit" className="bg-white rounded-xl border border-neutral-200 px-4">
              <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                Is the Diagnostic Fee credited toward the final bill?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 pb-4">
                The Diagnostic Fee is a separate charge that covers assessment and dispatch. It&apos;s not credited toward labour/materials.
                Think of it as paying for an expert to come, diagnose, and provide an accurate quote — which has value whether you proceed or not.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.section>

        {/* For Contractors Section */}
        <motion.section {...fadeIn} className="bg-neutral-100 rounded-2xl p-6 border border-neutral-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-neutral-200 rounded-xl flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-neutral-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                For Contractors
              </h3>
              <ul className="space-y-2 text-neutral-600 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Estimate ranges help set customer expectations before you arrive.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>If a quote is above the estimate range, select reasons and add notes — customers will see them.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Clear pricing builds trust and improves acceptance rates.</span>
                </li>
              </ul>
              <Link to="/become-contractor" className="inline-flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-black mt-4">
                Learn about becoming a ZOLT Pro <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </motion.section>

        {/* Platform Fee Details */}
        <motion.section {...fadeIn} className="bg-white rounded-2xl p-6 border border-neutral-200">
          <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            About Our Platform Fee
          </h3>
          <div className="space-y-4 text-neutral-600">
            <p>
              ZOLT retains a <strong>6% platform fee</strong> from the Diagnostic Fee you pay upfront. 
              This small fee covers:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>24/7 customer support</li>
              <li>Secure payment processing</li>
              <li>Contractor vetting and insurance verification</li>
              <li>Smart pricing estimates and job matching</li>
              <li>Platform maintenance and improvements</li>
            </ul>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-900">
                    No additional fees from final job cost
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Unlike other platforms, we do NOT take any percentage from labour or materials. 
                    The 6% from your Diagnostic Fee is our only charge.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fadeIn} className="text-center py-8">
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Get Started?
          </h3>
          <p className="text-neutral-600 mb-6">
            See estimate ranges for your job in under 60 seconds.
          </p>
          <Link to="/">
            <Button className="h-14 px-8 bg-black hover:bg-neutral-800 text-white rounded-xl text-lg">
              Get Your Quote <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </motion.section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-neutral-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-neutral-500">
          <p>© 2026 ZOLT X Limited. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/" className="hover:text-neutral-700">Home</Link>
            <Link to="/become-contractor" className="hover:text-neutral-700">Become a Pro</Link>
            <a href="#" className="hover:text-neutral-700">Privacy</a>
            <a href="#" className="hover:text-neutral-700">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
